import { PLATFORM_SKILLS_PATH } from './platformSkills'

// The Fleet Editor's agentless AREAS (ADR-111 IA-3/IA-6/IA-9): the surfaces that
// have no owning agent — no rail selection, no facet tabs. Kept out of
// FleetEditor.tsx so the component file only exports components (react-refresh)
// and so this logic is unit-testable without mounting the editor.

/** The Fleet Editor's create-an-agent surface (ADR-111 IA-9). One exact path. */
export const NEW_AGENT_PATH = '/fleet-editor/agents/new'

/** The areas that have NO owning agent: no rail selection, no facet tabs. */
export type FleetArea = 'resources' | 'data' | 'new-agent' | 'platform-skills'

/**
 * fleetAreaFor maps a pathname to the agentless area it belongs to, or null when
 * the path is agent-scoped (or the section root). The shell uses it to decide
 * whether an agent is in play at all — which in turn empties the header facet
 * tabs, suppresses the default-landing redirect, and stops any facet mounting.
 *
 * The rule for adding a fourth area is whether its first segment owns the whole
 * subtree below it. `resources` and `data` do — nothing else can ever live under
 * either — so they match by PREFIX and keep working if one grows a subpath.
 * `new-agent` cannot: it sits inside `agents/`, the namespace of every agent, so
 * it is matched EXACTLY (after stripping a trailing slash, which React Router
 * accepts). That is what lets an agent literally named `new` still work —
 * /fleet-editor/agents/new/workflows is that agent's Workflows facet, not the
 * create surface.
 */
/** The Data area's shareable entity path (ADR-111 IA-6). */
export const DATA_AREA_PATH = '/fleet-editor/data'

/**
 * dataSetFromPath reads the set ref out of /fleet-editor/data/<ref>, or returns
 * undefined for the bare area.
 *
 * A splat rather than a `:param` because a set ref is not one path segment: it
 * carries a version (`asana-sections@v1`) and may carry ADR-125 D10's reserved
 * `org/` namespace for core-owned artifacts (`org/fleet-channels@v1`), which
 * contains a slash. Reassembling it from the tail keeps both shapes addressable
 * with one route.
 *
 * Decoded per segment, so a ref written with encoded characters survives the
 * round trip through the address bar.
 */
export function dataSetFromPath(pathname: string): string | undefined {
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  if (!path.startsWith(DATA_AREA_PATH + '/')) return undefined
  const tail = path.slice(DATA_AREA_PATH.length + 1)
  if (!tail) return undefined
  return tail.split('/').map(decodeURIComponent).join('/')
}

/** The address of one data set — the thing an operator copies to share it. */
export function dataSetPath(setRef: string): string {
  const encoded = setRef.split('/').map(encodeURIComponent).join('/')
  return `${DATA_AREA_PATH}/${encoded}`
}

export function fleetAreaFor(pathname: string): FleetArea | null {
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  if (path.startsWith('/fleet-editor/resources')) return 'resources'
  if (path.startsWith('/fleet-editor/data')) return 'data'
  if (path === PLATFORM_SKILLS_PATH || path.startsWith(`${PLATFORM_SKILLS_PATH}/`)) return 'platform-skills'
  if (path === NEW_AGENT_PATH) return 'new-agent'
  return null
}
